import type { useGT } from 'gt-react';
import { PricelistEventAction } from '@erp/shared-types';
import type { PricelistEventDTO } from '@erp/rpc';

type Translate = ReturnType<typeof useGT>;
type EventMetadata = Record<string, unknown>;

function assignedLabel(gt: Translate, metadata: EventMetadata): string {
  const { companyName } = metadata;
  return typeof companyName === 'string'
    ? gt('Assigned to {company}', {
        context: 'pricelist history: assigned to a company',
        company: companyName,
      })
    : gt('Assigned to a company', {
        context: 'pricelist history: assigned to a company, name unknown',
      });
}

function extractionRetriedLabel(
  gt: Translate,
  metadata: EventMetadata,
): string {
  const count = metadata.tableCount;
  return typeof count === 'number'
    ? gt('CSV re-extracted: {count} tables', {
        context: 'pricelist history: extraction re-run with table count',
        count: String(count),
      })
    : gt('CSV re-extracted', {
        context: 'pricelist history: extraction re-run',
      });
}

function csvEditedLabel(gt: Translate, metadata: EventMetadata): string {
  const count = metadata.rowCount;
  return typeof count === 'number'
    ? gt('CSV edited: {count} rows saved', {
        context: 'pricelist history: edited CSV saved with row count',
        count: String(count),
      })
    : gt('CSV edited', {
        context: 'pricelist history: edited CSV saved',
      });
}

function divisionPriceChangesLabel(
  gt: Translate,
  metadata: EventMetadata,
): string {
  const { divisions } = metadata;
  const codes = Array.isArray(divisions)
    ? divisions.filter((code): code is string => typeof code === 'string')
    : [];
  return codes.length > 0
    ? gt('Division price change updated: {divisions}', {
        context: 'pricelist history: per-division price change edited',
        divisions: codes.join(', '),
      })
    : gt('Division price change updated', {
        context: 'pricelist history: per-division price change edited',
      });
}

function tariffFlagLabel(gt: Translate, metadata: EventMetadata): string {
  return metadata.to === true
    ? gt('Marked as resulting from a tariff change', {
        context: 'pricelist history: tariff flag turned on',
      })
    : gt('No longer marked as resulting from a tariff change', {
        context: 'pricelist history: tariff flag turned off',
      });
}

function anticipatedChangeLabel(
  gt: Translate,
  metadata: EventMetadata,
): string {
  const { to } = metadata;
  return typeof to === 'string'
    ? gt('Anticipated vendor change set to {percent}%', {
        context: 'pricelist history: anticipated vendor percentage set',
        percent: to,
      })
    : gt('Anticipated vendor change cleared', {
        context: 'pricelist history: anticipated vendor percentage cleared',
      });
}

function effectiveDateLabel(gt: Translate, metadata: EventMetadata): string {
  const { to } = metadata;
  return typeof to === 'string'
    ? gt('Effective date set to {date}', {
        context: 'pricelist history: effective date set',
        date: to,
      })
    : gt('Effective date cleared', {
        context: 'pricelist history: effective date cleared',
      });
}

function erpUploadDateLabel(gt: Translate, metadata: EventMetadata): string {
  const { to } = metadata;
  return typeof to === 'string'
    ? gt('AS/400 upload date set to {date}', {
        context: 'pricelist history: AS/400 upload date set',
        date: to,
      })
    : gt('AS/400 upload date cleared', {
        context: 'pricelist history: AS/400 upload date cleared',
      });
}

function receivedDateLabel(gt: Translate, metadata: EventMetadata): string {
  const { to } = metadata;
  return typeof to === 'string'
    ? gt('Received date set to {date}', {
        context: 'pricelist history: vendor received date set',
        date: to,
      })
    : gt('Received date cleared', {
        context: 'pricelist history: vendor received date cleared',
      });
}

// The stored value → what the history line calls it. Mirrors the details
// card's option labels without importing its component module.
const RECEIVED_AS_EVENT_LABELS: Record<string, string> = {
  notice: 'Notice',
  file: 'File',
  notice_file: 'Notice + file',
};

function receivedAsLabel(gt: Translate, metadata: EventMetadata): string {
  const { to } = metadata;
  const label =
    typeof to === 'string' ? RECEIVED_AS_EVENT_LABELS[to] : undefined;
  return label !== undefined
    ? gt('Received as set to {value}', {
        context: 'pricelist history: what the vendor sent was set',
        value: label,
      })
    : gt('Received as cleared', {
        context: 'pricelist history: what the vendor sent was cleared',
      });
}

function excludeFromLogLabel(gt: Translate, metadata: EventMetadata): string {
  return metadata.to === true
    ? gt('Excluded from the price file log', {
        context: 'pricelist history: kept out of the log export',
      })
    : gt('Returned to the price file log', {
        context: 'pricelist history: put back into the log export',
      });
}

function versionRestoredLabel(gt: Translate, metadata: EventMetadata): string {
  const { versionNumber } = metadata;
  return typeof versionNumber === 'number'
    ? gt('Restored version {version}', {
        context: 'pricelist history: tables restored to a numbered version',
        version: String(versionNumber),
      })
    : gt('Restored a previous version', {
        context: 'pricelist history: tables restored, version number unknown',
      });
}

function notesLabel(gt: Translate, metadata: EventMetadata): string {
  return metadata.cleared === true
    ? gt('Notes cleared', { context: 'pricelist history: notes emptied' })
    : gt('Notes updated', { context: 'pricelist history: notes edited' });
}

function deletedLabel(gt: Translate): string {
  return gt('Pricelist deleted', {
    context: 'pricelist history: pricelist removed from the pricelist views',
  });
}

// One label formatter per pricelist history action.
const EVENT_LABELS: Record<
  PricelistEventAction,
  (gt: Translate, metadata: EventMetadata) => string
> = {
  [PricelistEventAction.Assigned]: assignedLabel,
  [PricelistEventAction.Deleted]: deletedLabel,
  [PricelistEventAction.ExtractionRetried]: extractionRetriedLabel,
  [PricelistEventAction.CsvEdited]: csvEditedLabel,
  [PricelistEventAction.DivisionPriceChangesUpdated]: divisionPriceChangesLabel,
  [PricelistEventAction.TariffChangeFlagChanged]: tariffFlagLabel,
  [PricelistEventAction.AnticipatedChangeUpdated]: anticipatedChangeLabel,
  [PricelistEventAction.EffectiveDateUpdated]: effectiveDateLabel,
  [PricelistEventAction.ErpUploadDateUpdated]: erpUploadDateLabel,
  [PricelistEventAction.ReceivedAsUpdated]: receivedAsLabel,
  [PricelistEventAction.ReceivedDateUpdated]: receivedDateLabel,
  [PricelistEventAction.ExcludeFromLogChanged]: excludeFromLogLabel,
  [PricelistEventAction.NotesUpdated]: notesLabel,
  [PricelistEventAction.VersionRestored]: versionRestoredLabel,
};

/** Human-readable line for one pricelist history event. */
export function pricelistEventLabel(
  gt: Translate,
  event: PricelistEventDTO,
): string {
  return EVENT_LABELS[event.action](gt, event.metadata ?? {});
}
